import {Injectable} from '@angular/core';
import {environment} from 'src/environments/environment';

@Injectable()
export class BreadcrumbGraphService {

  private readonly storageKey: string;  // local storage key
  private graph: Map<string, string[]>; // destination -> sources

  constructor() {
    this.storageKey = `ZAPPDEV_BREADCRUMB_GRAPH_${environment.appId}`;
    this.loadGraph();
  }

  /**
   * Adds an edge to the graph.
   */
  addEdge(from: string, to: string) {
    if (from === to) {
      return;
    }
    if (!this.graph.has(to)) {
      this.graph.set(to, [from]);
    } else {
      let sources = this.graph.get(to);
      if (sources && !sources.includes(from)) {
        sources.push(from);
      }
    }
    this.saveGraph();
  }

  /**
   * Search a route in the graph. This is basically a cycle detection algorithm based on BFS.
   */
  search(route: string): string[] {
    const queue: { node: string, path: string[] }[] = [];
    queue.add({node: route, path: [route]});
    while (queue.length > 0) {
      const {node, path} = queue.shift();
      if (!this.graph.has(node)) {
        // Found terminal node
        return path.reverse();
      }
      const sources = this.graph.get(node);
      for (const source of sources) {
        if (path.includes(source)) {
          // Found cycle
          return path.reverse();
        }
        queue.push({node: source, path: [...path, source]});
      }
    }
    alert("BreadcrumbGraphService: Impossible search branch");
  }

  /**
   * Saves the graph to local storage.
   */
  private saveGraph() {
    const graphObject: {[key: string]: string[]} = {};
    this.graph.forEach((value, key) => {
      graphObject[key] = value;
    });
    const graphString = JSON.stringify(graphObject);
    localStorage.setItem(this.storageKey, graphString);
  }

  /**
   * Loads the graph from local storage.
   */
  private loadGraph() {
    const graphString = localStorage.getItem(this.storageKey);
    try {
      if (graphString) {
        const graphObject = JSON.parse(graphString);
        this.graph = new Map<string, string[]>();
        Object.entries(graphObject).forEach(([key, value]) => {
          if (Array.isArray(value) && value.every(item => typeof item === 'string')) {
            this.graph.set(key, value as string[]);
          } else {
            // Throw an exception if the value is not a string array
            throw new Error(`Invalid value type for key '${key}': ${typeof value}`);
          }
        });
      } else {
        this.graph = new Map();
      }
    } catch (error) {
      console.error("Error parsing the graph from local storage:", error);
      localStorage.removeItem(this.storageKey); // Clear the local storage key on error
      this.graph = new Map(); // Initialize the graph as an empty map
    }
  }

}
